// @flow

import * as React from 'react';

import type { NotifyAction } from '@gandi/notifications';
import { fetchOneOrganization } from '@gandi/redux-organization';
import type { FetchOneOrganizationAction } from '@gandi/redux-organization';
import type { Organization } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';
import { Button, Spinner } from '@gandi/ui';

import { SubmissionError } from 'gandi.react-form';

import type { PreFetchArgs, WithRouterProps } from '../../../../types';
import CustomerForm from '../../components/Form';

import type { EditCustomerAction } from '../../modules/customers';
import type { CustomerDataForm, CustomerDetail, UrlParams } from '../../types';

export type PresenterStateProps = {
  currentOrganizationId: string,
  isEditingCustomer: boolean,
  profile: CustomerDetail | Organization,
};

export type PresenterDispatchProps = {
  editCustomer: EditCustomerAction<Promise<any>>,
  fetchOneOrganization: FetchOneOrganizationAction,
  notifyDanger: NotifyAction,
  notifySuccess: NotifyAction,
};

export type PresenterInjectedProps = {
  translator: TranslatorProps,
  ...WithRouterProps<UrlParams>,
};

export type PresenterProps = {
  ...PresenterStateProps,
  ...PresenterDispatchProps,
  ...PresenterInjectedProps,
};

export default class Presenter extends React.Component<PresenterProps> {
  backUrl: string = `/cart/${this.props.params.cartId}/configure/account-choice`;

  static async preFetch({
    dispatch,
    params,
  }: PreFetchArgs<{}, UrlParams>): Promise<void> {
    return dispatch(fetchOneOrganization(params.customerId));
  }

  componentDidMount() {
    if (!this.props.profile) {
      this.props.fetchOneOrganization(this.props.params.customerId);
    }
  }

  handleSubmit: (data: CustomerDataForm) => any = (data: CustomerDataForm) => {
    const { __ } = this.props.translator;

    // if we are comming from preFetch
    // cart status may not be up to date
    // however the profile has ancestors
    // where we can extract the reserler id
    // mandatory to save the customer
    //
    // $FlowFixMe[prop-missing] ancestors is not declared
    // $FlowFixMe[incompatible-use]
    const resellerId = this.props.profile.ancestors[0]?.id;

    return this.props
      .editCustomer(
        // if we are comming from preFetch the currentOrganizationId
        // may not be set to the resellerId
        resellerId || this.props.currentOrganizationId,
        this.props.params.customerId,
        data,
      )
      .then(
        () => {
          this.props.notifySuccess(__('Customer successfully updated!'));
          this.props.navigate(this.backUrl);
        },
        error => {
          const message =
            typeof error.response.data.message === 'string'
              ? error.response.data.message
              : __('Unable to update the customer.');
          this.props.notifyDanger(message);
          throw new SubmissionError(error.errors);
        },
      )
      .catch(err => {
        if (err instanceof SubmissionError) {
          throw err;
        }
        this.props.notifyDanger(
          __('An unknown error occurred. Please try again later.'),
        );
      });
  };

  render(): React.Node {
    const {
      translator: { __ },
    } = this.props;

    const buttonsForm = [
      <Button to={this.backUrl} grouped kind="outline" key="cancel">
        {__('Cancel')}
      </Button>,
      <Button
        // $FlowFixMe[prop-missing]
        form={CustomerForm.FORM_ID}
        grouped
        key="submit"
        disabled={this.props.isEditingCustomer}
      >
        {__('Save')}
      </Button>,
    ];

    const formProps = {
      buttons: buttonsForm,
      initialValues: this.props.profile,
      isCreation: false,
      onSubmit: this.handleSubmit,
    };

    if (!this.props.profile) {
      return <Spinner centered />;
    }

    return <CustomerForm {...formProps} />;
  }
}
